import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { NewsletterOptIn } from "@/components/NewsletterOptIn";
import { trackEvent } from "@/lib/analytics";
import Seo from "@/components/Seo";

const QUESTIONS = {
  Communication: [
    "I set clear expectations with players and parents before the season starts.",
    "I give feedback that kids actually understand and can act on.",
    "I handle a tough parent conversation without it derailing my week.",
  ],
  Organization: [
    "Every practice I run has a plan before I walk onto the field.",
    "My team's logistics — schedules, forms, gear — rarely fall through the cracks.",
    "I can adjust a session on the fly when numbers or time change.",
  ],
  "Mental Performance": [
    "I know how to reset a kid who just made a mistake and shut down.",
    "I build confidence routines, not just skills.",
    "I stay composed under pressure so my players can too.",
  ],
  "Parent Management": [
    "I've defined the line between coaching and parenting for my own kid.",
    "I redirect sideline behavior before it becomes a problem.",
    "Playing-time conversations don't rattle me.",
  ],
  "Athlete Wellbeing": [
    "I notice when a kid's effort drop is about more than the sport.",
    "I prioritize long-term enjoyment over short-term winning.",
    "I know when something is above my pay grade and needs a real adult conversation.",
  ],
};

const CATS = Object.keys(QUESTIONS);
const SCALE = [
  { v: 1, label: "Not me" },
  { v: 2, label: "Rarely" },
  { v: 3, label: "Sometimes" },
  { v: 4, label: "Usually" },
  { v: 5, label: "That's me" },
];

export default function Assessment() {
  const nav = useNavigate();
  const [step, setStep] = useState(0); // 0..4 categories, 5 = details
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ email: "", name: "", is_org: false, org_name: "", coach_count: "", newsletter_opt_in: false });
  const [busy, setBusy] = useState(false);

  const isDetails = step === CATS.length;
  const cat = CATS[step];

  const setAns = (cat, idx, v) => {
    setAnswers((p) => ({ ...p, [cat]: { ...(p[cat] || {}), [idx]: v } }));
  };

  const catComplete = (c) => QUESTIONS[c].every((_, i) => (answers[c] || {})[i]);

  const next = () => {
    if (!isDetails && !catComplete(cat)) {
      toast.error("Answer all three to keep going.");
      return;
    }
    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  };

  const submit = async () => {
    if (!form.email) { toast.error("We need an email to build your dashboard."); return; }
    setBusy(true);
    const payload = {
      email: form.email,
      name: form.name || null,
      newsletter_opt_in: form.newsletter_opt_in,
      answers: Object.fromEntries(CATS.map((c) => [c, QUESTIONS[c].map((_, i) => (answers[c] || {})[i])])),
      b2b: form.is_org ? { is_org: true, org_name: form.org_name || null, coach_count: form.coach_count ? Number(form.coach_count) : null } : { is_org: false },
    };
    try {
      const { data } = await api.post("/assessment", payload);
      if (form.newsletter_opt_in) trackEvent("newsletter_signup", { source: "assessment" });
      nav(`/assessment/result/${data.account_id}`, { state: data });
    } catch {
      toast.error("Couldn't score that. Try again.");
      setBusy(false);
    }
  };

  const progress = Math.round((step / (CATS.length + 1)) * 100);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-12" data-testid="assessment-page">
      <Seo title="Free Coach Readiness Assessment" brand="VoluntoldHQ" description="Got handed a clipboard? Take the free Coach Readiness Assessment and find out where you stand before the first practice." path="/assessment" />
      <span className="kicker">Coach Readiness Assessment</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-3xl lg:text-5xl text-[#1E1E1E] leading-[1.05]">Where do you stand?</h1>
      <p className="mt-3 text-[#6E6E6E]">Five areas, honest answers. Scout scores it and hands you one next step — not a lecture.</p>

      <div className="mt-6 h-1 bg-[#D3CFC0]">
        <div className="h-full bg-[#E8A33D] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="assessment-progress" />
      </div>

      {!isDetails ? (
        <div className="mt-10" data-testid={`assessment-step-${step}`}>
          <div className="flex items-baseline justify-between border-b-2 border-[#1E1E1E] pb-2">
            <h2 className="font-head font-semibold uppercase text-2xl text-[#1E1E1E]">{cat}</h2>
            <span className="text-xs text-[#6E6E6E] font-head uppercase tracking-wide">{step + 1} / {CATS.length}</span>
          </div>
          <div className="mt-6 space-y-8">
            {QUESTIONS[cat].map((q, i) => (
              <div key={i} data-testid={`question-${step}-${i}`}>
                <p className="text-[#1E1E1E] text-[15px] mb-3">{q}</p>
                <div className="grid grid-cols-5 gap-2">
                  {SCALE.map((s) => {
                    const active = (answers[cat] || {})[i] === s.v;
                    return (
                      <button
                        key={s.v}
                        onClick={() => setAns(cat, i, s.v)}
                        data-testid={`answer-${step}-${i}-${s.v}`}
                        className={`py-2 px-1 border-2 text-center transition-colors ${active ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] bg-[#E7E3D9] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}
                      >
                        <div className="font-head text-lg">{s.v}</div>
                        <div className="text-[10px] leading-tight mt-0.5">{s.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-10" data-testid="assessment-details-step">
          <h2 className="font-head font-semibold uppercase text-2xl text-[#1E1E1E] border-b-2 border-[#1E1E1E] pb-2">Last thing</h2>
          <p className="mt-4 text-[#6E6E6E] text-sm">Where should Scout send your results?</p>
          <div className="mt-5 space-y-4">
            <input className="w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]"
              placeholder="First name (optional)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="assessment-name-input" />
            <input type="email" required className="w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]"
              placeholder="you@email.com" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="assessment-email-input" />

            <label className="flex items-center gap-3 cursor-pointer pt-2">
              <input type="checkbox" checked={form.is_org} onChange={(e) => setForm({ ...form, is_org: e.target.checked })}
                data-testid="assessment-org-checkbox" className="w-5 h-5 accent-[#2D2D2D]" />
              <span className="text-[#1E1E1E] text-[15px]">I coach for an organization, club, or league.</span>
            </label>
            <NewsletterOptIn checked={form.newsletter_opt_in} onChange={(v) => setForm({ ...form, newsletter_opt_in: v })} testId="assessment-newsletter-optin" />

            {form.is_org && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-8" data-testid="assessment-org-fields">
                <input className="border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]"
                  placeholder="Organization name" value={form.org_name}
                  onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="assessment-org-name-input" />
                <input type="number" min="1" className="border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]"
                  placeholder="How many coaches?" value={form.coach_count}
                  onChange={(e) => setForm({ ...form, coach_count: e.target.value })} data-testid="assessment-coach-count-input" />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between border-t border-[#D3CFC0] pt-6">
        <button onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0}
          className="text-[#6E6E6E] font-head uppercase tracking-wide text-sm disabled:opacity-30 hover:text-[#1E1E1E]" data-testid="assessment-back-btn">
          ← Back
        </button>
        {!isDetails ? (
          <button onClick={next} data-testid="assessment-next-btn"
            className="bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-sm px-8 py-3 hover:bg-[#242424] transition-colors">
            Continue
          </button>
        ) : (
          <button onClick={submit} disabled={busy} data-testid="assessment-submit-btn"
            className="bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-8 py-3 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
            {busy ? "Scoring…" : "Get my score"}
          </button>
        )}
      </div>
    </div>
  );
}
